import React, { useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';

/**
 * AdSenseUnit — single source of truth for placing Google AdSense slots
 * across the site. Only renders for free / guest users (paid VIPs and
 * admins never see ads). Each slot ID is provisioned in our AdSense
 * console under publisher `ca-pub-7888737267155230`.
 *
 * Available variants:
 *   - "display"       → 8913829532 — responsive display ad
 *   - "in-article"    → 8717554260 — inserted INSIDE blog post bodies
 *   - "in-feed"       → 7404472591 — inserted INTO blog list / index pages
 *   - "multiplex"     → 8103915989 — auto-relaxed multi-ad unit (page-level)
 *
 * Usage:
 *   <AdSenseUnit type="in-article" />
 */
const SLOTS = {
  display: '8913829532',
  'in-article': '8717554260',
  'in-feed': '7404472591',
  multiplex: '8103915989',
};

const CLIENT = 'ca-pub-7888737267155230';

const AdSenseUnit = ({ type = 'display', className = '' }) => {
  const { user } = useAuth();
  const insRef = useRef(null);
  const pushed = useRef(false);

  const isVip = user?.subscription_tier && user.subscription_tier !== 'free';
  const isAdmin = user?.role === 'admin' || user?.is_admin;
  const shouldHide = isVip || isAdmin;

  useEffect(() => {
    if (shouldHide || pushed.current) return;
    if (!window.adsbygoogle) return;
    pushed.current = true;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      // ad blocker / SDK race — fail silently
    }
  }, [shouldHide]);

  if (shouldHide) return null;

  const slot = SLOTS[type] || SLOTS.display;

  // Per-variant <ins> attribute set
  if (type === 'multiplex') {
    return (
      <div className={`adsense-wrap my-4 ${className}`} data-testid={`adsense-${type}`}>
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-format="autorelaxed"
          data-ad-client={CLIENT}
          data-ad-slot={slot}
        />
      </div>
    );
  }

  if (type === 'in-article') {
    return (
      <div className={`adsense-wrap my-6 ${className}`} data-testid={`adsense-${type}`}>
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block', textAlign: 'center' }}
          data-ad-layout="in-article"
          data-ad-format="fluid"
          data-ad-client={CLIENT}
          data-ad-slot={slot}
        />
      </div>
    );
  }

  if (type === 'in-feed') {
    return (
      <div className={`adsense-wrap my-4 ${className}`} data-testid={`adsense-${type}`}>
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-format="fluid"
          data-ad-layout-key="-h3-1h-1d+67+4x"
          data-ad-client={CLIENT}
          data-ad-slot={slot}
        />
      </div>
    );
  }

  // default: responsive display ad
  return (
    <div className={`adsense-wrap my-4 ${className}`} data-testid={`adsense-${type}`}>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={CLIENT}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
};

export default AdSenseUnit;
